import axios from '@/lib/axios'
import { useUserStore } from '@/stores/user'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { storeToRefs } from 'pinia'
import { MaybeRef, onBeforeUnmount, onMounted, watch } from 'vue'
import { RouteLocationRaw, Router, useRoute, useRouter } from 'vue-router'
import { ToastInterface, useToast } from 'vue-toastification'

import { User } from '@/modules/users/interfaces/user'
import { helpers, required, requiredIf } from '@vuelidate/validators'
import { get } from '@vueuse/core'
import { error } from 'console'
import { create, update } from 'lodash'

interface Props {
	immediate?: boolean
	params?: Params
	id?: string
}

interface Params extends Omit<Partial<User>, 'id'> {
	id?: MaybeRef<string | number>
	roleId?: string | number
	includes?: string[]
}

interface RedirectPayload {
	params: CreateParams
	redirect?: RouteLocationRaw
}

export type CreateParams = {
	[K in keyof User]?: K extends (
		| 'name'
		| 'lastname'
		| 'birthDate'
		| 'email'
		| 'role_id'
		| 'jobTitle'
		| 'company_id'
	) ?
		User[K]
	:	User[K] | undefined
}

const getUser = async (
	id: MaybeRef<string | number>,
	params: Params
): Promise<User> => {
	const { data } = await axios.get<User>(`/api/internal/users/${get(id)}`, {
		params,
	})

	return data
}

const updateUser = async (
	params: Partial<Params>,
	toast: ToastInterface
): Promise<User | void> => {
	try {
		const { id } = params
		const { data } = await axios.put(`/api/internal/users/${id}`, params)

		toast.success('Usuario actualizado con éxito')
		return data
	} catch (err) {
		toast.error('Error al actualizar el usuario. Intente nuevamente.')
	}
}

const useInternalUser = (props: Props) => {
	const toast = useToast()
	const router = useRouter()
	const { params: routeParams } = useRoute()
	const { immediate = true, params = {} } = props
	const { user, createForm } = storeToRefs(useUserStore())
	const { setUser, setCreateFormValue, resetCreateForm } = useUserStore()

	const updateMutation = useMutation((params: Partial<Params>) =>
		updateUser(params, toast)
	)

	onMounted(() => resetCreateForm())

	const rulesUserValidation = {
		name: {
			required: helpers.withMessage('El nombre es requerido', required),
		},
		lastname: {
			required: helpers.withMessage('El apellido es requerido', required),
		},
		birthDate: {
			required: helpers.withMessage(
				'La fecha de nacimiento es requerida',
				required
			),
		},
		gender: {
			required: helpers.withMessage('El género es requerido', required),
		},
		email: {
			required: helpers.withMessage(
				'El correo es requerido',
				requiredIf(() => {
					return createForm.value.haveAccess || createForm.value.companyId === 0
				})
			),
		},
		roleId: {
			required: helpers.withMessage('El rol es requerido', required),
		},
	}

	const userId = params.id || (routeParams.id as string) || '0'

	const { data, isLoading, isFetching, refetch } = useQuery<User>(
		['user-internal', Number(userId)],
		() => getUser(props.id || userId, params),
		{ retry: false, enabled: immediate }
	)

	watch(
		data,
		(value) => {
			if (value) {
				setUser(value)
			}
		},
		{ immediate: true }
	)

	onBeforeUnmount(() => resetCreateForm())

	return {
		user,
		isLoading,
		isFetching,
		refetch,
		createForm,

		rulesUserValidation,

		setCreateFormValue,
		resetCreateForm,

		updateUserMutation: updateMutation,
		updateUser: updateMutation.mutate,
		updateUserAsync: updateMutation.mutateAsync,
		updateUserLoading: updateMutation.isLoading,
	}
}

export default useInternalUser
